import { useState, useEffect, useRef } from 'react'
import { AiOutlinePlayCircle } from 'react-icons/ai';
import { ImCancelCircle } from 'react-icons/im';

import { startLoader } from './Loader/loader.js'

import SlideThumbnail from './SlideThumbnail'
import QuestionsContainer from './QuestionsContainer'
import InfoContainer from './InfoContainer'
import QRCodeGeneratorContainer from './QRCodeGeneratorContainer';
import Tutorial from './Tutorial';
import VideoOverlay from './VideoOverlay';
import Pdf from './Pdf'
import KursButton from './KursButton';
import PowerPointOverlay from './PowerPointOverlay';
import FeedbackForm from './FeedbackForm.js';
import Spinner from './Spinner.js';
import LoaderOverlay from './Loader/LoaderOverlay.js';

import 'bootstrap/dist/css/bootstrap.min.css';
import './../css/presentation.css'
import './../css/video-controls.css'
import './../css/question-container.css'
import './../css/info-container.css'
import './../css/responsive.css'
import './../css/feedback-form.css'
import './../css/interessensgebiete-selection.css'
import './Loader/loader.css'

import videoPoster from './../img/video-poster.png'

import { cme_kurs } from './Global/Kurs'
import { this_device } from './Global/Device'
import { isOnServer, onlyQuestions, supportInfo } from './Global/constants'
import { errorHandling, jumpToCoursePart, getURLParameter } from './Global/functions'
import { mainVideo } from './Global/Video'
import { mainPresentation } from './Global/Presentation'
import * as GoogleAnalytics from './Global/GoogleAnalytics'
import cmeducationLogo from './../img/cmeducation_logo.png'

export const Main = () => {

    /*********************************
     *      DEFINE STATES
     *********************************/
    const [initialized, setInitialized] = useState(false);

    // slides -> powerpoint-slides
    const [slides, setSlides] = useState();
    const [slidesAmount, setSlidesAmount] = useState();

    const [videoPath, setVideoPath] = useState();
    const [pdfPath, setPdfPath] = useState();

    // always 0 if course has 1 part
    const [courseIndex, setCourseIndex] = useState();
    // current video-time in 5-second steps
    const [currentVideoTime, setCurrentVideoTime] = useState();
    // ref-Spiegel von currentVideoTime, damit Effekte darauf zugreifen können
    // ohne bei jeder Änderung erneut zu laufen
    const currentVideoTimeRef = useRef();
    useEffect(() => { currentVideoTimeRef.current = currentVideoTime; }, [currentVideoTime]);

    // -- Google Analytics
    // ga4 - current video-progress
    const [videoProgress, setVideoProgress] = useState();
    // ga4 - which progress got sent (prevent double entrys in analytics params)
    const [sentProgress, setSentProgress] = useState({})


    // 1. -> initialize app
    useEffect(() => { init() }, []);
    const init = async () => {
        console.log('%cInitialisiere cMed!', 'color: #ff3300ff');

        // INIT MAIN KURS-OBJECT
        await cme_kurs.init();

        if (cme_kurs && cme_kurs.initialized && !cme_kurs.Warning) {
            // -- set page title
            document.title = cme_kurs.KursTitel;

            // -- GOOGLE-ANALYTICS -> send page-view event
            GoogleAnalytics.sendPageView();

            // -- errorhandling mainly for slides, video
            if (!cme_kurs.isBigVideo && !cme_kurs.isPdf && !cme_kurs.isPowerPoint) {
                // return if errorhandling return false                
                if (!errorHandling()) return;
            }

            // -- set current videotime
            var storedVideoTime = JSON.parse(window.localStorage.getItem('currentVideoTime' + cme_kurs.kurs_key));
            if (storedVideoTime) setCurrentVideoTime(storedVideoTime);
            else setCurrentVideoTime(0)

            // -- set course-index
            var storedCourseIndex = JSON.parse(window.localStorage.getItem('courseIndex' + cme_kurs.kurs_key));
            if (storedCourseIndex) setCourseIndex(storedCourseIndex);
            else setCourseIndex(0);
        }
        else {
            // not published yet
            document.querySelector('#progstat').innerHTML = `Es konnte keine Verbindung zum Server hergestellt werden.<br><br>Überprüfen Sie Ihre Internetverbindung.<br><br>Sollte das Problem weiter bestehen, wenden Sie sich bitte an unseren Support unter: ${supportInfo}`
        }
    }

    // 2. -> course-index change
    useEffect(() => {
        if (cme_kurs.initialized && (courseIndex === 0 || courseIndex)) {
            console.log("update course index to: ", courseIndex)
            // set course-index in localstorage
            window.localStorage.setItem('courseIndex' + cme_kurs.kurs_key, courseIndex);

            if (setSources(courseIndex)) setInitialized(true);
        }
    }, [courseIndex]);

    // 3. -> on completed initialization
    useEffect(() => {
        if (initialized && cme_kurs.initialized) {

            // init presentation object
            mainPresentation.init();

            // special handling powerpoint
            if (cme_kurs.isPowerPoint) {
                // initialize video-class if powerpoint
                console.log('Initialisiere Video-Klasse für PowerPoint'); mainVideo.init();
                // set first thumbnail as active if kurstyp is powerpoint (other types define active thumbnail when video loaded)
                document.querySelector('.single-thumbnail').click();
            }

            // see what parameter got passed via url
            checkUrlParameter();

            // bugfix for ios <= 14
            mainVideo.videoLoadBugfix(currentVideoTimeRef.current);

            startLoader();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized])


    // -- set video-time in localstorage
    useEffect(() => {
        if (cme_kurs.initialized && initialized && (currentVideoTime || currentVideoTime === 0)) {
            window.localStorage.setItem('currentVideoTime' + cme_kurs.kurs_key, currentVideoTime);
        }
    }, [currentVideoTime, initialized]);


    // -- on update video-progress -> send to google-analytics
    useEffect(() => {
        if (cme_kurs.initialized && (videoProgress || videoProgress === 0)) {
            GoogleAnalytics.videoProgressUpdate(videoProgress, sentProgress, setSentProgress);
        }
    }, [videoProgress, sentProgress]);


    // -- set video/chart/pdf sources depending on course-index
    const setSources = (index) => {
        try {
            console.log("set sources to index: ", index);

            // -- set slides
            if (!cme_kurs.isBigVideo && !cme_kurs.isPdf) {
                // -- which slide
                const tempSlides = cme_kurs.allSlides[index];
                setSlides(tempSlides);
                setSlidesAmount(tempSlides.length);
            }

            // -- set pdf path
            if (cme_kurs.isPdf) {
                setPdfPath(process.env.PUBLIC_URL + '/assets/pdf/' + cme_kurs.PDF);
            }
            // -- set video path
            else if (!cme_kurs.isPowerPoint) setVideoPath(process.env.PUBLIC_URL + '/assets/videos/' + cme_kurs.Video[index].src);

            return true;
        }
        catch (e) {
            console.log('Fehler bei Initialisierung', e);
            return false;
        }
    }


    // -- check for url-parameter
    const checkUrlParameter = () => {
        const urlParams = getURLParameter();

        // check for maximized questions
        var maximizeQuestions = urlParams.maxQuestions;
        if (maximizeQuestions === "true") {
            var toggleSlidesContainerIcon = document.querySelector('.reiter-container .resize-grow-grey');
            var toggleRightPanelIcon = document.querySelector('.video_header');

            if (toggleSlidesContainerIcon !== null) toggleSlidesContainerIcon.click();
            if (toggleRightPanelIcon !== null) toggleRightPanelIcon.click();
        }


        // check for special tracking for google analytics
        const utm_medium = urlParams.utm_medium ? urlParams.utm_medium : null;
        const utm_source = urlParams.utm_source ? urlParams.utm_source : null;
        const utm_campaign = urlParams.utm_campaign ? urlParams.utm_campaign : null;
        const utm_id = urlParams.utm_id ? urlParams.utm_id : null;

        if (utm_medium || utm_source || utm_campaign || utm_id) {

            const utmObject = {
                "VNR": cme_kurs.VNR,
                "KursTitel": cme_kurs.KursTitel,
                "KursKey": cme_kurs.KursKey,
                "KursTyp": cme_kurs.KursTyp,
                "Sponsor": cme_kurs.Sponsor_Name,
                "utm_medium": utm_medium,
                "utm_source": utm_source,
                "utm_campaign": utm_campaign,
                "utm_id": utm_id
            }

            GoogleAnalytics.sendToGa4('aussendienst_tracking', utmObject);
        }


        // check for qr-code params
        const ad_lastname = urlParams.ad_lastname;
        const ad_firstname = urlParams.ad_firstname;
        const ad_email = urlParams.ad_email;

        if (ad_lastname && ad_firstname && ad_email) {

            const qrObject = {
                "VNR": cme_kurs.VNR,
                "KursTitel": cme_kurs.KursTitel,
                "KursKey": cme_kurs.KursKey,
                "KursTyp": cme_kurs.KursTyp,
                "Sponsor": cme_kurs.Sponsor_Name,
                "ad_lastname": ad_lastname,
                "ad_firstname": ad_firstname,
                "ad_email": ad_email,
            }

            GoogleAnalytics.sendToGa4('qr_code_tracking', qrObject);
        }
    }


    // -- video-listener
    const onLoadedMetadata = (e) => {
        mainVideo.init(e.currentTarget, slides, currentVideoTime);
    }
    const onTimeUpdate = () => {
        mainVideo.videoTimeUpdate(slides, currentVideoTime, setCurrentVideoTime, videoProgress, setVideoProgress);
    }
    const onEnded = () => {
        mainVideo.videoEnded(courseIndex + 1, () => jumpToCoursePart(courseIndex + 1, setCourseIndex, setCurrentVideoTime));
    }

    return (
        <>
            {/* --------- loader overlay --------- */}
            {/* wird von startLoader() in loader.js per DOM-IDs (overlay, progress, progstat, welcome) angesteuert */}
            <LoaderOverlay />

            {/* lets go if all of this is initialized */}
            {cme_kurs.initialized && initialized
                && (courseIndex === 0 || courseIndex)
                && (pdfPath || videoPath || cme_kurs.isPowerPoint)
                && ((currentVideoTime === 0 || currentVideoTime) || cme_kurs.isPdf || cme_kurs.isPowerPoint) ?

                <div id="main-container" fullwidth={(cme_kurs.isBigVideo || cme_kurs.isPdf || onlyQuestions) ? "true" : "false"} questionsonly={(onlyQuestions) ? "true" : "false"} ispowerpoint={(cme_kurs.isPowerPoint) ? "true" : "false"}>   {/* if bigvideo or pdf set full width*/}

                    {/* --------- slides, info, questions --------- */}
                    <div className="presentation-container">

                        {/* Header */}

                        <div className='header_cmeducation'>
                            <a href="https://cmeducation.de/" target="_blank" rel="noreferrer">
                                <img src={cmeducationLogo} className="cmeducation-logo-header" alt="cmEducation-Logo" />
                            </a>
                        </div>

                        {/* presentation -> Slides and Controls */}
                        {(!onlyQuestions) ?
                            <div className="slides-container container-shadow-down">

                                {/* dont display this section if its a pdf */}
                                {(!cme_kurs.isPdf) ?
                                    <>

                                        {/* overlay for next part of course -> when video ends */}
                                        {(cme_kurs.partsAmount > (courseIndex + 1)) ?
                                            <span className="nextpart-btn-overlay" onClick={(e) => e.stopPropagation()} hidden>
                                                <KursButton
                                                    buttonName={
                                                        <>
                                                            <div>Teil {courseIndex + 2} beginnt in <span className="next-part-countdown"></span> Sekunden</div>
                                                        </>
                                                    }
                                                    onClick={() => jumpToCoursePart(courseIndex + 1, setCourseIndex, setCurrentVideoTime)}
                                                    btnIcon={<AiOutlinePlayCircle />}
                                                />
                                                <div className="countdown-bar"></div>
                                                <KursButton buttonName={"Abbrechen"} onClick={() => (jumpToCoursePart(courseIndex, setCourseIndex, setCurrentVideoTime))} btnIcon={<ImCancelCircle />} />
                                            </span>
                                            : <></>}


                                        {/* ----- overlay for video controls ----- */}
                                        {(!cme_kurs.isPowerPoint)
                                            ?
                                            <VideoOverlay slides={slides} courseIndex={courseIndex} setCourseIndex={setCourseIndex} setCurrentVideoTime={setCurrentVideoTime} slidesAmount={slidesAmount} />
                                            :
                                            <PowerPointOverlay slides={slides} courseIndex={courseIndex} setCourseIndex={setCourseIndex} setCurrentVideoTime={setCurrentVideoTime} slidesAmount={slidesAmount} />
                                        }


                                        {/* main presentation */}
                                        {(cme_kurs.isBigVideo) ?
                                            // CASE 1 ----- BigVideo
                                            <video
                                                playsInline
                                                preload="auto"
                                                id="main-video"
                                                className="slide"
                                                src={videoPath}
                                                poster={videoPoster}

                                                onLoadedMetadata={onLoadedMetadata}
                                                onTimeUpdate={onTimeUpdate}
                                                onEnded={onEnded}

                                                onClick={() => mainVideo.toggleOverlay()}
                                            />
                                            :
                                            // CASE 2 ------ Standard
                                            // CASE 4 ------ PowerPoint
                                            <>
                                                {/* standard slide */}
                                                <img
                                                    id="slide"
                                                    className="slide"
                                                    src={slides[0].source}
                                                    alt="main-presentation-slide"
                                                    onClick={() => mainVideo.toggleOverlay()}
                                                />

                                                {/* if chart is a video */}
                                                <video id="video-slide" className="slide" src={slides[0].source} onClick={() => mainVideo.toggleOverlay()} hidden />
                                            </>
                                        }

                                    </>
                                    :
                                    // CASE 3 ----- PDF
                                    <div className="pdf-container">
                                        <Pdf
                                            url={pdfPath}
                                            runningInApp={this_device.isTouchDevice ? true : false}
                                            togglePdf={(e) => mainPresentation.togglePdf(e)}
                                        />
                                    </div>

                                }

                            </div> // end of slides-container
                            : <></>
                        }


                        {/* Buttons to select infos, questions, or qr-generator */}
                        <div className="reiter-container">

                            {/* Wissensfragen */}
                            {cme_kurs.wissensfragenActive ? <div className="reiter reiter-active fragen-reiter" onClick={(e) => mainPresentation.toggleReiter(e)}>Wissensfragen</div> : ""}

                            {/* Kursinfo */}
                            <div className={"reiter reiter-next info-reiter " + ((cme_kurs.wissensfragenActive) ? "" : "reiter-active")} onClick={(e) => mainPresentation.toggleReiter(e)}>Kursinfo</div>

                            {/* Feedback */}
                            {isOnServer ? <div className={"reiter reiter-next feedback-reiter"} onClick={(e) => mainPresentation.toggleReiter(e)}>Feedback</div> : ""}


                            {/* small nav-icons on right side */}
                            <div className="nav-btn-container">

                                {/* ? - Button */}
                                <Tutorial />

                                {/* Maximize Reiter */}
                                <div className="resize-grow-grey" onClick={(e) => mainPresentation.toggleSlidesContainer(e)}></div>
                            </div>

                        </div>


                        {/* main-container for course information, questions and qr-code-generator */}
                        <div className="info-questions-container">

                            <InfoContainer isHidden={cme_kurs.wissensfragenActive} />

                            {(cme_kurs.wissensfragenActive) ? <QuestionsContainer /> : ""}

                            {(cme_kurs.qrCodeGeneratorActive) ? <QRCodeGeneratorContainer /> : ""}

                            {isOnServer ?
                                <div className='feedback-container' hidden>
                                    <FeedbackForm />
                                </div>
                                : ""
                            }

                        </div>

                    </div>



                    {/* --------- video and thumbnail-overview --------- */}
                    {/*  dont display right panel in bigVideo and pdf --> CASE 1 & 3 */}
                    {(cme_kurs.isBigVideo || cme_kurs.isPdf || onlyQuestions) ?
                        <>
                            {/* CASE 1 ----- BigVideo */}
                            {/* CASE 3 ----- PDF */}
                            {/* nothing gets displayed */}
                        </>
                        :
                        // CASE 4 ----- PowerPoint
                        // CASE 2 ----- Standard
                        <div className="video-thumbnail-container container-shadow">

                            {/* main video */}
                            <div className="video-container shadow">

                                {/* toggle button for right panel */}
                                <div className="video_header" onClick={(e) => mainPresentation.toggleRightPanel(e)}>
                                    <span className="kursinhalte">Kursinhalte</span>
                                    <div className="video_header_close_btn close-grey"></div>
                                </div>


                                {/* video in right panel */}
                                {/* dont show video if powerpoint */}
                                {(!cme_kurs.isPowerPoint) ?
                                    <>
                                        {/* CASE 2 ----- Standard */}
                                        <video
                                            playsInline
                                            preload="auto"
                                            id="main-video"
                                            src={videoPath}
                                            poster={videoPoster}

                                            onLoadedMetadata={onLoadedMetadata}
                                            onTimeUpdate={onTimeUpdate}
                                            onEnded={onEnded}

                                            onClick={(e) => mainVideo.togglePlayPause(e)}
                                        />
                                        {/* small referenten info */}
                                        {cme_kurs.Referenten[0] ?
                                            <div className="video_footer">
                                                {cme_kurs.Referenten.map((singleReferent, index) => (
                                                    <span key={'small-referent-info-' + index} >{singleReferent.Titel ? singleReferent.Titel + " " : ""}{singleReferent.Vorname} {singleReferent.Nachname}<br /></span>
                                                ))}
                                            </div>
                                            :
                                            <></>
                                        }
                                    </>
                                    : <>

                                        {/* CASE 4 ----- PowerPoint */}
                                        {/* doesnt get displayed    */}
                                    </>}


                            </div>

                            {/* slide-thumbnails */}
                            <div className="thumbnail-container">
                                {slides.map((thisSlide, index) => (

                                    <SlideThumbnail
                                        key={`thumbnail-key-${courseIndex}-${index + 1}`}
                                        id={`thumbnail-${courseIndex}-${index + 1}`}

                                        thisSlide={thisSlide}
                                    />

                                ))}
                            </div>
                        </div>
                    }

                </div>

                :

                <Spinner />
            }
        </>
    )
}

export default Main